import { Subject, takeUntil } from 'rxjs';

import {
    AfterContentInit, AfterViewInit, booleanAttribute, ChangeDetectionStrategy, ChangeDetectorRef,
    Component, ContentChild, ContentChildren, EventEmitter, inject, Input, Optional, Output,
    QueryList, Self, signal
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import { OptionComponent } from './option.component';

@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectComponent implements ControlValueAccessor, AfterContentInit, AfterViewInit {
  @Input({ transform: booleanAttribute }) multiple!: boolean;
  @Input({ transform: booleanAttribute }) required!: boolean;
  @Input({ required: true }) label!: string;
  @Input({ transform: booleanAttribute }) topDropdown = false;
  @Input() labelsTranslatable = false;
  @Input() isContainerPopup = false;
  @ContentChildren(OptionComponent) optionComps: QueryList<OptionComponent> | undefined;
  @Output() optionsLoaded = new EventEmitter<QueryList<OptionComponent>>();
  protected readonly isMobile = window.innerWidth < 768 || screen.width < 768;
  constructor(@Optional() @Self() public control: NgControl, private cdr: ChangeDetectorRef,) {
    if (control != null) {
      this.control.valueAccessor = this;
    }
  }

  ngAfterContentInit() {
  }

  ngAfterViewInit() {
  }

  protected readonly closeDropdown = () => {
    if (this.isMobile) return;
    if (this.isActive) this.onTouch(true);
    this.isActive = false;
  };

  private onChange = (input: string) => {};
  private onTouch = (isTouched: boolean) => {};
  registerOnChange(fn: (input: string) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: (isTouched: boolean) => void): void {
    this.onTouch = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    // if (isDisabled) this.selected = '';
    this._isDisabled.set(isDisabled);
  }

  writeValue(value: string) {
    this._selected.set(value);
    if (!this.optionComps) return;
    if (this.multiple) this.writeValueMultiple(value);
    else {
      const found = this.optionComps.find(optionComp => optionComp.value === value);
      this.displayValue = found ? found.content.nativeElement.innerText : '';
    }
  }

  private readonly writeValueMultiple = (value: string) => {
    const values = value.split(',');
    const found = this.optionComps?.filter(optionComp => values.includes(optionComp.value));
    if (!found) return;
    this.displayValue = found.map(option => option.content.nativeElement.innerText).join(',');
  };

  private readonly _isDisabled = signal(false);
  private readonly _selected = signal('');
  private readonly _isActive = signal(false);
  private readonly _displayValue = signal('');

  get displayValue() {
    return this._displayValue();
  }
  set displayValue(value: string) {
    if (!this.multiple || !this.displayValue) {
      this._displayValue.set(value);
      return;
    }
    if (!value && this.labelsTranslatable) {
      this._displayValue.set(value);
      return;
    }
    const displayValues = this.displayValue.split(',');
    let result!: string;
    if (displayValues.includes(value))
      result = displayValues.filter(_value => _value !== value).join(',');
    else result = [...displayValues, value].join(',');
    if (result.length === 0) return;
    this._displayValue.set(result);
  }

  get isDisabled() {
    return this._isDisabled();
  }

  get isActive() {
    return this._isActive();
  }
  set isActive(value: boolean) {
    this._isActive.set(value);
  }
  get selected() {
    return this._selected();
  }
  set selected(value: string) {
    this.onTouch(true);
    if (!this.multiple || !this.selected) {
      this._selected.set(value);
      this.onChange(value);
    } else this.setMultiple(value);
  }
  protected setTouched = () => this.onTouch(true);
  private setMultiple(value: string) {
    const selectedValues = this.selected.split(',');
    let result!: string;
    if (selectedValues.includes(value))
      result = selectedValues.filter(_value => _value !== value).join(',');
    else result = [...selectedValues, value].join(',');
    if (result.length === 0) return;
    this._selected.set(result);
    this.onChange(result);
  }
  private readonly translate = inject(TranslateService);
  private readonly destroy$ = new Subject<void>();
  ngOnInit() {
    if (!this.labelsTranslatable) return;
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.displayValue = '';
    });
  }
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
